import React, { useState, useEffect, useContext, useRef  } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Container, Typography, Grid, Card, CardContent, TextField, Box, Snackbar, Alert, Select, MenuItem, FormControl, InputLabel, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button as MuiButton, Button } from '@mui/material';
import { ViewModule, ViewList, Dashboard, Timeline, Add } from '@mui/icons-material'; // Import Add icon
import { LabelsContext } from '../context/LabelsContext';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import KanbanBoard from '../components/KanbanBoard'; // Import KanbanBoard component
import TimelinePopup from '../components/TimelinePopup'; // Import TimelinePopup component
import config from '../config/config'; // Import configuration
import { formatDate, formatNumber } from '../utils/formatterUtils'; // Import format utilities

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [riskLevelFilter, setRiskLevelFilter] = useState('');
  
  const [kanbanDialogOpen, setKanbanDialogOpen] = useState(false);
  const [timelineDialogOpen, setTimelineDialogOpen] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const navigate = useNavigate();
  const gridRef = useRef(null);
  const [gridApi, setGridApi] = useState(null); // State to store the grid API
  const [exportError, setExportError] = useState(false); // State to manage export error message

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const response = await axios.get(`${config.apiBaseUrl}/api/projects`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-TenantID': 'admin',
          },
        });
        setProjects(response.data);
      } catch (error) {
        console.error('Error fetching projects:', error);
      }
    };

    fetchProjects();
  }, []);

  const labels = useContext(LabelsContext);

// Add riskLevelFilter to the filtering logic
const filteredProjects = projects.filter(project =>
  project.project_name.toLowerCase().includes(searchTerm.toLowerCase()) &&
  (statusFilter === '' || project.status === statusFilter) &&
  (riskLevelFilter === '' || project.risk_level === riskLevelFilter) // Apply Risk Level Filter
);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleCreateNewProject = () => {
    navigate('/project-edit/new');
  };

  const handleProjectClick = (projectId) => {
    navigate(`/projects/${projectId}`);
  };

  const handleKanbanBoardClick = (projectId) => {
    setSelectedProjectId(projectId);
    setKanbanDialogOpen(true);
  };

  const handleTimelineClick = (projectId) => {
    setSelectedProjectId(projectId);
    setTimelineDialogOpen(true);
  };

  const handleKanbanDialogClose = () => {
    setKanbanDialogOpen(false);
    setSelectedProjectId(null);
  };

  const handleTimelineDialogClose = () => {
    setTimelineDialogOpen(false);
    setSelectedProjectId(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Ongoing':
        return '#FFEB3B'; // Yellow
      case 'Completed':
        return '#4CAF50'; // Green
      case 'Delayed':
        return '#F44336'; // Red
      default:
        return '#BDBDBD'; // Gray
    }
  };

  const StatusCellRenderer = (params) => {
    return (
      <div
        style={{
          backgroundColor: getStatusColor(params.value),
          color: '#fff',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {params.value}
      </div>
    );
  };

  const columns = [
    { headerName: labels?.projects?.project_name || "Project Name", field: 'project_name', sortable: true, filter: true, resizable: true, tooltipField: 'project_name' },
    { headerName: labels?.projects?.risk_level || "Risk Level", field: 'risk_level', sortable: true, filter: true, resizable: true, width: 120, tooltipField: 'risk_level' },
    { headerName: labels?.projects?.description || "Description", field: 'description', sortable: true, filter: true, resizable: true, width: 500, tooltipField: 'description' },
    { headerName: labels?.projects?.status || "Status", field: 'status', sortable: true, filter: true, resizable: true, cellRendererFramework: StatusCellRenderer, width: 100, tooltipField: 'status' },
    { headerName: labels?.projects?.location_id || "Location", field: 'location_id.locationName', sortable: true, filter: true, resizable: true, tooltipField: 'location_id.locationName' },
    { headerName: labels?.projects?.start_date || "Start Date", field: 'start_date', sortable: true, filter: true, resizable: true, valueFormatter: params => formatDate(params.value), tooltipField: 'start_date' },
    { headerName: labels?.projects?.planned_completion_date || "Planned Completion Date", field: 'planned_completion_date', sortable: true, filter: true, resizable: true, valueFormatter: params => formatDate(params.value), tooltipField: 'planned_completion_date' },
];

  const toggleViewMode = () => {
    setViewMode((prevMode) => (prevMode === 'grid' ? 'list' : 'grid'));
  };

const handleExport = () => {
  if (gridApi) {
    gridApi.exportDataAsCsv(); // Export grid data to CSV
  } else {
    setExportError(true); // Show error message to the user
  }
};

const handleExportErrorClose = () => {
  setExportError(false); // Close the Snackbar
};

  const onGridReady = (params) => {
    setGridApi(params.api); // Store the grid API when the grid is ready
  };

  return (
    <Container>
     <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
  <Typography variant="h5" color="#0099cc" gutterBottom>
    Projects
  </Typography>
  <Box display="flex" justifyContent="flex-end" gap={2}>
    <Button
      variant="contained"
      color="primary"
      startIcon={<Add />}
      onClick={handleCreateNewProject}
    >
      Create New Project
    </Button>
    <Button variant="contained" color="primary" onClick={handleExport}>
      Export to CSV
    </Button>
  </Box>
</Box>
      <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
        <TextField
          label="Search Projects"
          variant="outlined"
          fullWidth
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <FormControl variant="outlined" style={{ marginLeft: '16px', minWidth: 200 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            label="Status"
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            <MenuItem value="Ongoing">Ongoing</MenuItem>
            <MenuItem value="Completed">Completed</MenuItem>
            <MenuItem value="Delayed">Delayed</MenuItem>
          </Select>
        </FormControl>
        <FormControl variant="outlined" style={{ marginLeft: '16px', minWidth: 200 }}> {/* Risk Level Filter */}
    <InputLabel>Risk Level</InputLabel>
    <Select
      value={riskLevelFilter}
      onChange={(e) => setRiskLevelFilter(e.target.value)}
      label="Risk Level"
    >
      <MenuItem value="">
        <em>All</em>
      </MenuItem>
      <MenuItem value="High">High</MenuItem>
      <MenuItem value="Medium">Medium</MenuItem>
      <MenuItem value="Low">Low</MenuItem>
    </Select>
  </FormControl>
        <IconButton onClick={toggleViewMode}>
          {viewMode === 'list' ? <ViewList /> : <ViewModule />}
        </IconButton>
      </Box>
      {viewMode === 'list' ? (
       <Grid container spacing={3}>
       {filteredProjects.map((project) => (
         <Grid item xs={12} sm={6} md={4} lg={3} key={project.project_id}>
           <Card
             onClick={() => handleProjectClick(project.project_id)}
             sx={{
               cursor: 'pointer',
               '&:hover': {
                 boxShadow: 6, // Apply emboss effect on hover
               },
               position: 'relative', // Ensure the icon is positioned relative to the card
               backgroundColor: project.risk_level === 'High' ? '#ffefea' : 'inherit', // Set background color if risk_level is High
             }}
           >
             <CardContent>
               <Box display="flex" alignItems="center">
                 <Box
                   component="span"
                   sx={{
                     display: 'inline-block',
                     width: '12px',
                     height: '12px',
                     borderRadius: '50%',
                     backgroundColor: getStatusColor(project.status),
                     marginRight: '8px',
                   }}
                 ></Box>
                 <Typography variant="subtitle1">{project.project_name}</Typography>
               </Box>
               <Typography variant="body2" color="textSecondary">
                 {project.company_id.companyName}
               </Typography>
               <Typography variant="body2" color="textSecondary">
                 Start Date: {formatDate(project.start_date)}
               </Typography>
               <Typography variant="body2" color="textSecondary">
                 Planned Completion Date: {formatDate(project.planned_completion_date)}
               </Typography>
               <Typography variant="body2" color="textSecondary">
                 Risk Level: {project.risk_level}
               </Typography>
               <Tooltip title="View Kanban Board">
                 <IconButton
                   onClick={(e) => {
                     e.stopPropagation();
                     handleKanbanBoardClick(project.project_id);
                   }}
                   sx={{
                     position: 'absolute',
                     bottom: 8,
                     right: 40,
                     color: '#0099cc', // Set icon color to blue
                   }}
                 >
                   <Dashboard />
                 </IconButton>
               </Tooltip>
               <Tooltip title="View Timeline">
                 <IconButton
                   onClick={(e) => {
                     e.stopPropagation();
                     handleTimelineClick(project.project_id);
                   }}
                   sx={{
                     position: 'absolute',
                     bottom: 8,
                     right: 8,
                     color: '#0099cc', // Set icon color to blue
                   }}
                 >
                   <Timeline />
                 </IconButton>
               </Tooltip>
             </CardContent>
           </Card>
         </Grid>
       ))}
     </Grid>
      ) : (
        <div className="ag-theme-alpine" style={{ height:600, width: '100%' }}>
          <AgGridReact
            columnDefs={columns}
            rowData={filteredProjects}
            pagination={true}
            paginationPageSize={20}
            onRowClicked={(event) => handleProjectClick(event.data.project_id)}
            domLayout='normal'
            tooltipShowDelay={0}
            ref={gridRef} 
            onGridReady={onGridReady}
          />
        </div>
      )}
      <Dialog open={kanbanDialogOpen} onClose={handleKanbanDialogClose} maxWidth="lg" fullWidth>
        <DialogTitle>Kanban Board</DialogTitle>
        <DialogContent>
          {selectedProjectId && <KanbanBoard projectId={selectedProjectId} />}
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={handleKanbanDialogClose} color="primary">
            Close
          </MuiButton>
        </DialogActions>
      </Dialog>
      <TimelinePopup
        projectId={selectedProjectId}
        dialogOpen={timelineDialogOpen}
        handleDialogClose={handleTimelineDialogClose}
      />
         <Snackbar
      open={exportError}
      autoHideDuration={6000}
      onClose={handleExportErrorClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} // Ensure it's visible
    >
      <Alert onClose={handleExportErrorClose} severity="error" sx={{ width: '100%' }}>
        Switch to grid view to export data.
      </Alert>
    </Snackbar>
    </Container>
  );
};

export default Projects;